/**
 * Модуль для работы с маской телефона
 * Использует библиотеку intlTelInput для международного форматирования номеров
 */

export class PhoneMask {
  constructor(inputElement, options = {}) {
    this.phoneInput = inputElement;
    this.wrapper = inputElement.closest('.js-phone-input-wrapper');
    this.iti = null;
    this.phoneMaxLength = 15;
    
    // Опции по умолчанию
    this.options = {
      initialCountry: options.initialCountry || 'ru',
      language: options.language || 'ru',
      showValidation: options.showValidation !== false,
      ...options
    };
    
    // Сообщения об ошибках
    this.errorMessages = {
      ru: {
        REQUIRED: 'Обязательное поле',
        INCORRECT_PHONE: 'Некорректный телефон',
        TOO_SHORT: 'Номер слишком короткий',
        TOO_LONG: 'Номер слишком длинный',
        INVALID_COUNTRY_CODE: 'Неверный код страны',
        INVALID_LENGTH: 'Неверная длина номера'
      },
      us: {
        REQUIRED: 'Required',
        INCORRECT_PHONE: 'Phone incorrect',
        TOO_SHORT: 'Number too short',
        TOO_LONG: 'Number too long',
        INVALID_COUNTRY_CODE: 'Invalid country code',
        INVALID_LENGTH: 'Invalid number length'
      }
    };
    
    this.init();
  }
  
  init() {
    if (!this.phoneInput) return;
    
    // Инициализируем intlTelInput
    this.iti = window.intlTelInput(this.phoneInput, {
      loadUtils: () => import("https://cdn.jsdelivr.net/npm/intl-tel-input@25.3.0/build/js/utils.js"),
      initialCountry: this.options.initialCountry,
      separateDialCode: true,
      autoFormat: true,
      formatAsYouType: true,
      autoPlaceholder: "aggressive",
      strictMode: true,
      showSelectedDialCode: true,
      allowDropdown: true,
      customPlaceholder: (selectedCountryPlaceholder) => {
        return this.getPlaceholder(selectedCountryPlaceholder);
      }
    });
    
    // Ждем загрузки утилит
    this.iti.promise.then(() => {
      this.updatePlaceholder();
    });
    
    // Привязываем обработчики событий
    this.bindEvents();
  }
  
  bindEvents() {
    this.phoneInput.addEventListener('input', () => this.handleInput());
    this.phoneInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.phoneInput.addEventListener('countrychange', () => this.handleCountryChange());
  }
  
  handleInput() {
    if (!this.iti || !this.options.showValidation) return;
    
    // Убираем визуальную обратную связь, оставляем только очистку ошибок для валидных номеров
    if (this.phoneInput.value.trim() !== '' && this.iti.isValidNumber()) {
      this.clearError();
    }
  }
  
  handleKeydown(event) {
    const allowedKeys = [
      8, 9, 27, 13, // backspace, tab, escape, enter
      46, // delete
      35, 36, // home, end
      37, 38, 39, 40, // arrow keys
      32, // space
      43, 45, // +, -
      40, 41, // ( )
    ];
    
    // Allow: Ctrl/Cmd + A, C, V, X, Z
    if (event.ctrlKey || event.metaKey) {
      if ([65, 67, 86, 88, 90].includes(event.keyCode)) {
        return;
      }
    }
    
    // Allow special keys
    if (allowedKeys.includes(event.keyCode)) {
      return;
    }
    
    // Allow numbers
    if ((event.keyCode >= 48 && event.keyCode <= 57) || 
        (event.keyCode >= 96 && event.keyCode <= 105)) {
      return;
    }
    
    event.preventDefault();
  }
  
  handleCountryChange() {
    this.updatePlaceholder();
    this.clearError();
  }
  
  updatePlaceholder() {
    if (!this.iti) return;
    
    const countryData = this.iti.getSelectedCountryData();
    const exampleNumber = window.intlTelInputUtils ? 
      window.intlTelInputUtils.getExampleNumber(countryData.iso2, true, window.intlTelInputUtils.numberFormat.NATIONAL) : 
      null;
    
    const placeholder = this.getPlaceholder(exampleNumber);
    this.phoneInput.setAttribute('placeholder', placeholder);
  }
  
  getPlaceholder(exampleNumber) {
    // Возвращаем простой номер без префиксов
    if (exampleNumber) {
      // Убираем скобки и лишние символы, оставляем только цифры, пробелы и дефисы
      return exampleNumber.replace(/[()]/g, '').trim();
    }
    
    // Дефолтный placeholder в зависимости от языка
    const defaultPlaceholders = {
      ru: "912 345-67-89",
      us: "555 123-4567"
    };
    
    return defaultPlaceholders[this.options.language] || defaultPlaceholders.ru;
  }
  
  validate(showError = false) {
    if (!this.phoneInput.value.trim()) {
      if (showError) {
        this.showError(this.errorMessages[this.options.language].REQUIRED);
      }
      return false;
    }
    
    if (!this.iti) {
      // Fallback validation
      const digitsOnly = this.phoneInput.value.replace(/\D/g, "");
      const isValid = digitsOnly.length >= 5 && digitsOnly.length <= 15;
      
      if (!isValid && showError) {
        this.showError(this.errorMessages[this.options.language].INCORRECT_PHONE);
      }
      
      return isValid;
    }
    
    const isValid = this.iti.isValidNumber();
    
    if (!isValid && showError) {
      const errorCode = this.iti.getValidationError();
      let errorMessage = this.errorMessages[this.options.language].INCORRECT_PHONE;
      
      if (window.intlTelInputUtils) {
        const errorMap = {
          [window.intlTelInputUtils.validationError.TOO_SHORT]: 
            this.errorMessages[this.options.language].TOO_SHORT,
          [window.intlTelInputUtils.validationError.TOO_LONG]: 
            this.errorMessages[this.options.language].TOO_LONG,
          [window.intlTelInputUtils.validationError.INVALID_COUNTRY_CODE]: 
            this.errorMessages[this.options.language].INVALID_COUNTRY_CODE,
          [window.intlTelInputUtils.validationError.INVALID_LENGTH]: 
            this.errorMessages[this.options.language].INVALID_LENGTH
        };
        
        errorMessage = errorMap[errorCode] || errorMessage;
      }
      
      this.showError(errorMessage);
    }
    
    return isValid;
  }
  
  getFormattedNumber() {
    return this.iti ? this.iti.getNumber() : this.phoneInput.value;
  }
  
  showError(errorText) {
    if (!this.wrapper) return;
    
    this.wrapper.classList.add("form-info__error");
    
    if (errorText) {
      const inputField = this.wrapper.closest('.form-info__input-field');
      if (inputField) {
        const errorItem = inputField.querySelector('.form-info__error-field');
        if (errorItem) {
          errorItem.classList.add('form-info__error-field_visible');
          errorItem.textContent = errorText;
        }
      }
    }
  }
  
  clearError() {
    if (!this.wrapper) return;
    
    this.wrapper.classList.remove("form-info__error");
    
    const inputField = this.wrapper.closest('.form-info__input-field');
    if (inputField) {
      const errorItem = inputField.querySelector('.form-info__error-field');
      if (errorItem) {
        errorItem.classList.remove('form-info__error-field_visible');
        errorItem.textContent = '';
      }
    }
  }
  
  destroy() {
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
  }
}

// Функция для быстрой инициализации
export function initPhoneMask(inputElement, options = {}) {
  return new PhoneMask(inputElement, options);
} 